/**
 * Fragments de catalogue i18n par zone (paquet v0-p00-ouverture ; plan de réalisation v10 § 3.1-4).
 *
 * `locales/fr.json` et `locales/en.json` sont GELÉS pour le chantier v10 : une vague ajoute ses textes dans
 * `locales/<zone>.fr.json` et `locales/<zone>.en.json` (catalogues plats clé → texte, les deux langues
 * exigées, mêmes clés). Ils sont fusionnés au chargement à la base, sans jamais la redéfinir : une clé déjà
 * dans la base ou dans un autre fragment est refusée par un problème nommé (`CONFLIT_BASE`,
 * `CONFLIT_FRAGMENTS`), une clé sans son pendant dans l'autre langue aussi (`PARITE_FRAGMENT`).
 * `language.tsx` traduit avec `CATALOGUES_FUSIONNES` ; sans fragment, ce sont les catalogues de base eux-mêmes.
 */
import fr from './locales/fr.json'
import en from './locales/en.json'

export type Catalogue = Record<string, string>
export interface FragmentI18n { zone: string; fr: Catalogue; en: Catalogue }

function estCatalogue(valeur: unknown): valeur is Catalogue {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur)
    && Object.values(valeur).every((texte) => typeof texte === 'string')
}

export function fragmentsI18nDuDossier(modules: Record<string, unknown>): { fragments: FragmentI18n[]; problemes: string[] } {
  const zones = new Map<string, Partial<Record<'fr' | 'en', unknown>>>()
  const problemes: string[] = []
  for (const fichier of Object.keys(modules).sort()) {
    const correspondance = /^\.\/locales\/([a-z0-9]+(?:-[a-z0-9]+)*)\.(fr|en)\.json$/.exec(fichier)
    const zone = correspondance?.[1]
    const langue = correspondance?.[2]
    if (!zone || (langue !== 'fr' && langue !== 'en')) {
      problemes.push(`ZONE_INVALIDE : ${fichier}`)
      continue
    }
    const catalogues = zones.get(zone) ?? {}
    catalogues[langue] = modules[fichier]
    zones.set(zone, catalogues)
  }
  const fragments: FragmentI18n[] = []
  for (const zone of [...zones.keys()].sort()) {
    const catalogues = zones.get(zone)
    if (!catalogues || !Object.hasOwn(catalogues, 'fr') || !Object.hasOwn(catalogues, 'en')) {
      problemes.push(`FRAGMENT_INCOMPLET : ${zone} (fr + en)`)
    } else if (!estCatalogue(catalogues.fr) || !estCatalogue(catalogues.en)) {
      problemes.push(`CATALOGUE_INVALIDE : ${zone} (Record<string, string>)`)
    } else {
      fragments.push({ zone, fr: catalogues.fr, en: catalogues.en })
    }
  }
  return { fragments, problemes }
}

export function fusionnerCatalogues(base: { fr: Catalogue; en: Catalogue }, fragments: readonly FragmentI18n[]):
  { fr: Catalogue; en: Catalogue; problemes: string[] } {
  const resultat = { fr: { ...base.fr }, en: { ...base.en }, problemes: [] as string[] }
  const proprietaires = new Map<string, string>()
  // Comparaison lexicale explicite : l'ordre ne dépend pas de la locale de la machine.
  const tries = [...fragments].sort((a, b) => a.zone < b.zone ? -1 : a.zone > b.zone ? 1 : 0)
  for (const fragment of tries) {
    const cles = [...new Set([...Object.keys(fragment.fr), ...Object.keys(fragment.en)])].sort()
    for (const cle of cles) {
      if (!Object.hasOwn(fragment.fr, cle) || !Object.hasOwn(fragment.en, cle)) {
        resultat.problemes.push(`PARITE_FRAGMENT : ${fragment.zone} — ${cle}`)
      } else if (Object.hasOwn(base.fr, cle) || Object.hasOwn(base.en, cle)) {
        resultat.problemes.push(`CONFLIT_BASE : ${fragment.zone} — ${cle}`)
      } else if (proprietaires.has(cle)) {
        resultat.problemes.push(`CONFLIT_FRAGMENTS : ${fragment.zone} — ${cle} (${proprietaires.get(cle) ?? ''})`)
      } else {
        // defineProperty traite aussi les clés de catalogue telles que « __proto__ ».
        for (const langue of ['fr', 'en'] as const) {
          Object.defineProperty(resultat[langue], cle, {
            value: fragment[langue][cle], enumerable: true, configurable: true, writable: true,
          })
        }
        proprietaires.set(cle, fragment.zone)
      }
    }
  }
  return resultat
}

const chargement = fragmentsI18nDuDossier(import.meta.glob([
  './locales/*.fr.json', './locales/*.en.json', '!./locales/fr.json', '!./locales/en.json',
], { eager: true, import: 'default' }))
export const FRAGMENTS_I18N: readonly FragmentI18n[] = chargement.fragments
const fusion = fusionnerCatalogues({ fr, en }, FRAGMENTS_I18N)
// Sans extension, conserver les références historiques de la façade de traduction.
// La fonction de fusion reste pure et rend toujours des copies indépendantes.
export const CATALOGUES_FUSIONNES: { fr: Catalogue; en: Catalogue } = FRAGMENTS_I18N.length === 0
  ? { fr, en }
  : { fr: fusion.fr, en: fusion.en }
export const PROBLEMES_I18N: readonly string[] = [...chargement.problemes, ...fusion.problemes]
if (PROBLEMES_I18N.length > 0) console.error('[i18n]', PROBLEMES_I18N)
